/**
 * Work list row + shared type/priority/status color helpers.
 * Detail view rendering is in ./work_detail_render.ts.
 *
 * Row grammar (see work-row.css):
 *   [icon] [id+parent] [title]───[tags] [type] [prio] [status] [ts]
 *
 * The row uses CSS classes, not inline styles — keeps the column grid
 * predictable across rows (inline-style flex was causing status pills
 * to land at different x-positions depending on tag count).
 *
 * Motion: each row gets --stagger-i set inline; work-row.css uses that
 * to compute its animation-delay. Bounded at 50 (page size) so the
 * tail doesn't exceed ~600ms.
 */

import { esc } from '../../shared/rendering.js'
import { capitalizeLabel } from '../../types/domain.js'
import { action } from '../components/action-delegation.js'
import type { WorkItem } from '../../types/api.js'
import { renderWorkDetail } from './work_detail_render.js'

// ─── Color helpers — single source of truth for both list and detail ────────

const TYPE_COLOR: Record<string, string> = {
  epic: 'pur',
  story: 'cyn',
  task: 't2',
  bug: 'err',
}
const TYPE_ICON: Record<string, string> = {
  epic: '◆',
  story: '■',
  task: '▶',
  bug: '⚠',
}
const STATUS_COLOR: Record<string, string> = {
  open: 'cyn',
  in_progress: 'wrn',
  done: 'grn',
  cancelled: 't4',
  ready: 'grn',
}
const PRIO_COLOR: Record<string, string> = {
  critical: 'err',
  high: 'wrn',
  medium: 'cyn',
  low: 't4',
}

export function typeColor(type: string): string {
  return TYPE_COLOR[type] ?? 't3'
}
export function typeIcon(type: string): string {
  return TYPE_ICON[type] ?? '•'
}
export function statusColor(status: string): string {
  return STATUS_COLOR[status] ?? 't3'
}
export function prioColor(priority: string): string {
  return PRIO_COLOR[priority] ?? 't4'
}

// Legacy short-name aliases — kept so work_detail_render.ts (and the
// run view when it eventually ports) can import the short names.
export const tc = typeColor
export const sc = statusColor
export const pc = prioColor

// ─── Relative timestamp ─────────────────────────────────────────────────────

/**
 * Compact relative time — "2d", "5m", "3h". Targets 3-char max so the
 * timestamp column can be 54px wide.
 */
function shortRelTime(iso: string | null | undefined): string {
  if (!iso) {
    return '—'
  }
  const t = Date.parse(iso)
  if (!Number.isFinite(t)) {
    return '—'
  }
  const d = (Date.now() - t) / 1000
  if (d < 60) {
    return `${Math.floor(d)}s`
  }
  if (d < 3600) {
    return `${Math.floor(d / 60)}m`
  }
  if (d < 86400) {
    return `${Math.floor(d / 3600)}h`
  }
  if (d < 2592000) {
    return `${Math.floor(d / 86400)}d`
  }
  if (d < 31536000) {
    return `${Math.floor(d / 2592000)}mo`
  }
  return `${Math.floor(d / 31536000)}y`
}

// ─── Row rendering ──────────────────────────────────────────────────────────

export function renderWorkList(items: readonly WorkItem[]): string {
  if (items.length === 0) {
    return `<div class="empty" style="padding:48px 16px;text-align:center;color:var(--t3);font-size:13px">
      <div style="margin-bottom:10px;font-family:var(--mono);font-size:11px;color:var(--t4);letter-spacing:0.05em;text-transform:uppercase">No matching items</div>
      <button class="wi-filter-btn" ${action('clearWorkFilters')} style="padding:5px 14px">Clear filters</button>
    </div>`
  }
  const rows = items.map((item, i) => renderRow(item, i)).join('')
  return `<div class="wi-list">${rows}</div>`
}

function renderRow(item: WorkItem, i: number): string {
  const tColor = typeColor(item.type)
  const sColor = statusColor(item.status)
  const pColor = prioColor(item.priority)

  // Token-scoped color vars — set per row so the CSS can pick them up
  // without a giant `.wi-row.epic`, `.wi-row.bug` matrix.
  const styleVars =
    `--stagger-i:${i};` +
    `--wi-icon-c:var(--${tColor});` +
    `--wi-type-c:var(--${tColor});` +
    `--wi-type-bg:var(--${tColor}-d);` +
    `--wi-prio-c:var(--${pColor});` +
    `--wi-status-c:var(--${sColor});` +
    `--wi-status-bg:var(--${sColor}-d)`

  const parent = item.parent_id
    ? `<span class="wi-id-parent" title="parent">↳ ${esc(item.parent_id)}</span>`
    : ''

  // Tags: at most 3 inline to keep row compact; overflow indicator if more.
  // Tag buttons carry their own data-action; the document-level
  // action-delegation handler in components/action-delegation.ts uses
  // `closest('[data-action]')` and calls `e.stopPropagation()` itself
  // when a handler matches, so the row's `data-action="navigate"` is
  // not also fired. No inline onclick="event.stopPropagation()" needed.
  const visibleTags = (item.tags ?? []).slice(0, 3)
  const hiddenCount = Math.max(0, (item.tags ?? []).length - visibleTags.length)
  const tagChips = visibleTags
    .map(
      (t) =>
        `<button class="wi-tag" ${action('toggleWorkTag', t)} aria-label="Filter by tag: ${esc(t)}">${esc(t)}</button>`,
    )
    .join('')
  const overflowChip =
    hiddenCount > 0 ? `<span class="wi-tag" style="pointer-events:none">+${hiddenCount}</span>` : ''

  const ts = shortRelTime(item.updated_at ?? item.created_at)

  return `<a class="wi-row" style="${styleVars}" role="link" tabindex="0" aria-label="${esc(item.title)}" ${action('navigate', `/work/${item.id}`)}>
    <span class="wi-icon" aria-hidden="true">${typeIcon(item.type)}</span>
    <span class="wi-id">
      <span class="wi-id-main">${esc(item.id)}</span>
      ${parent}
    </span>
    <span class="wi-title">${esc(item.title)}</span>
    <span class="wi-tags">${tagChips}${overflowChip}</span>
    <span class="wi-type">${esc(capitalizeLabel(item.type))}</span>
    <span class="wi-prio"><span class="wi-prio-dot" aria-hidden="true"></span>${esc(capitalizeLabel(item.priority))}</span>
    <span class="wi-status">${esc(capitalizeLabel(item.status.replace(/_/g, ' ')))}</span>
    <span class="wi-ts" title="${esc(item.updated_at ?? item.created_at ?? '')}">${esc(ts)}</span>
  </a>`
}

// Re-export detail render so existing importers (work.ts) keep working.
export { renderWorkDetail }
